// 📄 components/StepIsuParams.tsx
import React from 'react';
import { AlertCircle } from 'lucide-react';
import { InvoiceStatusFormData } from '../../../../../services/commerce/integrationService';

interface StepIsuParamsProps {
  isuBteEnabled: boolean;
  invoiceDocTypeIsu: string;
  avoidDuplicateIsuPp: boolean;
  onChange: (field: keyof InvoiceStatusFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepIsuParams({ isuBteEnabled, invoiceDocTypeIsu, avoidDuplicateIsuPp, onChange, isInvalid }: StepIsuParamsProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">SPRO</span>
          <span>Integration with Other SAP Components → SAP Ariba Cloud Integration Gateway → Application-Specific Settings → Invoice Status Update → Maintain Parameters</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-blue-400">1. Maintain Parameters (Habilitar Estatus de Facturas)</h3>
        <p className="text-sm text-gray-400">
          Habilita el envío del estatus de la factura hacia SAP Ariba mediante el Business Transaction Event (BTE) y evita duplicados en el post-procesamiento.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-white">Parámetro <code className="bg-black/30 px-1 rounded font-mono text-blue-400">ISU_BTE_ENABLED</code></label>
            <p className="text-xs text-gray-400 mt-0.5">Valor bajo (Low) requerido: <code className="bg-black/30 px-1 rounded font-mono">X</code></p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input type="checkbox" checked={isuBteEnabled || false} onChange={(e) => onChange('isuBteEnabled', e.target.checked)} className="sr-only peer" />
            <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>

        <div className="border-t border-white/5 pt-3">
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Parámetro <code className="bg-black/30 px-1 rounded font-mono text-blue-400">INVOICE_DOC_TYPE</code> <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={invoiceDocTypeIsu}
            onChange={(e) => onChange('invoiceDocTypeIsu', e.target.value.toUpperCase())}
            placeholder="RE"
            maxLength={2}
            className={`w-full max-w-[140px] bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500 ${
              isInvalid && !invoiceDocTypeIsu ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>

        <div className="border-t border-white/5 pt-3 flex items-center justify-between gap-4">
          <div>
            <label className="block text-sm font-medium text-white">Parámetro <code className="bg-black/30 px-1 rounded font-mono text-blue-400">AVOID_DUPLICATE_ISU_PP</code></label>
            <p className="text-xs text-gray-400 mt-0.5">Evita reenvíos duplicados del estatus por reprocesamiento</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input type="checkbox" checked={avoidDuplicateIsuPp || false} onChange={(e) => onChange('avoidDuplicateIsuPp', e.target.checked)} className="sr-only peer" />
            <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
          </label>
        </div>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-blue-400 flex gap-3 text-sm">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Consistencia con el canal de Facturas</p>
          <p className="text-xs text-blue-400/80 mt-0.5">
            <code className="bg-black/30 px-1 rounded">INVOICE_DOC_TYPE</code> debe coincidir con el Document Type configurado en el canal Facturas (paso 3, Program Parameters & Posting Keys).
          </p>
        </div>
      </div>
    </div>
  );
}
